import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Button,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { UnitForm } from './UnitForm'
import { useCreateUnit } from '../hooks/useUnits'
import type { UnitFormData } from '../validation'

interface UnitCreateModalProps {
  isOpen: boolean
  onClose: () => void
  defaultResidenceId?: string
  onSuccess?: (unitId: string) => void
}

export function UnitCreateModal({
  isOpen,
  onClose,
  defaultResidenceId,
  onSuccess,
}: UnitCreateModalProps) {
  const { t } = useTranslation()
  const createUnit = useCreateUnit()

  const handleSubmit = async (data: UnitFormData) => {
    try {
      const newUnit = await createUnit.mutateAsync(data)
      onClose()
      if (onSuccess) {
        onSuccess(newUnit.id)
      }
    } catch (error) {
      console.error('Error creating unit:', error)
      alert(t('common.error'))
    }
  }

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="lg">
      <DialogBackdrop />
      <DialogPositioner
        display="flex"
        alignItems="center"
        justifyContent="center"
        position="fixed"
        inset="0"
      >
        <DialogContent margin="auto" maxWidth="600px" maxHeight="85vh" width="full">
          <DialogHeader>
            <DialogTitle>{t('units.create')}</DialogTitle>
          </DialogHeader>
          <DialogCloseTrigger />
          <DialogBody pb={6} overflowY="auto" maxHeight="calc(85vh - 180px)">
            <UnitForm
              onSubmit={handleSubmit}
              onCancel={onClose}
              isLoading={createUnit.isPending}
              showActions={false}
              formId="unit-create-form"
              defaultResidenceId={defaultResidenceId}
            />
          </DialogBody>
          <DialogFooter
            position="sticky"
            bottom={0}
            bg="white"
            borderTopWidth="1px"
            display="flex"
            gap={3}
            justifyContent="flex-end"
            p={4}
          >
            <Button variant="outline" onClick={onClose} disabled={createUnit.isPending}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="unit-create-form"
              colorPalette="brand"
              loading={createUnit.isPending}
            >
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
